import { memo, FunctionComponent, useState, useEffect } from 'react';
import { useConfig } from '../../../../eliot-components/src/ConfigProvider/hooks';
import {
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  FormGroup,
} from '@material-ui/core';
import { Org } from '../../../Orgs/types';
import ButtonProgress from '../../../../eliot-components/src/ButtonProgress/ButtonProgress';
import { useUpdateOrgMutation } from '../../../../store/api-slice';
import { useSnackbar } from 'notistack';
import { parseErrorMessage } from '../../../../eliot-components/src/utils/helpers';

interface Props {
  isOpen: boolean;
  onClose(): void;
  onUpdated(): void;
  org: Org;
}

const TestDevicesDialog: FunctionComponent<Props> = memo(
  ({ isOpen, org, onClose }) => {
    const { apiBaseURL } = useConfig();
    const [updateOrg, { isLoading }] = useUpdateOrgMutation();
    const availableModles = [
      'BC800',
      'BP800',
      'D40G',
      'BG800',
      'BT105',
      'BT005',
      'PM100',
      'GW9017',
      'SmartLink',
      'BS-2001-G1',
      'PS300C-RPM',
      'LS802-GP',
      'BM300C-RPM',
    ];
    const [selectedModels, setSelectedModel] = useState<string[]>([]);

    useEffect(() => {
      if (org.allowTestModels) {
        org.allowTestModels.forEach((e: string) => {
          setSelectedModel((item) => [...item, e]);
        });
      }
    }, [org]);

    const { enqueueSnackbar } = useSnackbar();

    const handleSubmit = async () => {
      const orgObj = {
        id: org.id,
        name: org.name,
        allowTestModels: selectedModels,
      };
      updateOrg({ org: orgObj, apiBaseURL }).then((resp: any) => {
        if (!resp.error) {
          enqueueSnackbar('Organization updated');
          onClose();
          return;
        }
        enqueueSnackbar(parseErrorMessage(resp.error), { variant: 'error' });
      });
    };
    return (
      <>
        <Dialog aria-labelledby="dialog-title" open={isOpen} onClose={onClose}>
          <DialogTitle id="dialog-title">Allow Test Devices</DialogTitle>

          <DialogContent>
            <FormGroup>
              {availableModles.map((e: string) => (
                <FormControlLabel
                  key={`label-${e}`}
                  control={
                    <Checkbox
                      key={`checkbox-${e}`}
                      checked={selectedModels.includes(e)}
                      onChange={(c) => {
                        if (selectedModels.includes(e)) {
                          setSelectedModel((item) =>
                            item.filter((item) => item !== e)
                          );
                        } else {
                          setSelectedModel((item) => [...item, e]);
                        }
                      }}
                      name={e}
                    />
                  }
                  label={e}
                />
              ))}
            </FormGroup>
          </DialogContent>

          <DialogActions>
            <Button onClick={onClose}>Cancel</Button>
            <ButtonProgress
              type="button"
              onClick={handleSubmit}
              variant="contained"
              color="primary"
              isLoading={isLoading}
              data-testid="submit-update"
              disabled={isLoading}
            >
              Submit
            </ButtonProgress>
          </DialogActions>
        </Dialog>
      </>
    );
  }
);

export default TestDevicesDialog;
